import React, { useContext, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { Context } from '../../../App';

const VIEWBOX_WIDTH = 860;
const PLOT_ONLY_VIEWBOX_WIDTH = 540;
const VIEWBOX_HEIGHT = 430;
const CONTENT_Y_SHIFT = 0;
const SUMMARY_X = 569;
const SUMMARY_WIDTH = 248;
const SUMMARY_CARD_WIDTH = 118;
const SUMMARY_CARD_HEIGHT = 40;
const SUMMARY_CARD_COL_GAP = 12;
const SUMMARY_CARD_ROW_GAP = 8;
const SUMMARY_BADGE_Y = 44;
const SUMMARY_METRICS_Y = 82;
const SUMMARY_FOCUS_Y = 278;
const SUMMARY_FOCUS_HEIGHT = 92;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const formatValue = (value, digits = 3, unit = '') => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '-';
  return `${numeric.toFixed(digits)}${unit ? ` ${unit}` : ''}`;
};

const buildColors = (theme) =>
  theme === 'dark'
    ? {
        canvas: '#12181f',
        panel: '#18212b',
        plot: '#10171e',
        border: '#3a4756',
        grid: '#23303d',
        text: '#dde7f0',
        muted: '#9fb0bf',
        columnFill: '#b88845',
        columnStroke: '#f3d29b',
        tributaryFill: 'rgba(88, 164, 255, 0.18)',
        tributaryStroke: '#7cb7ff',
        spacing: '#4fd1c5',
        diameter: '#f59e0b',
        center: '#fb7185',
        lattice: '#526171',
        statusGoodFill: '#173f2b',
        statusGoodStroke: '#4ade80',
        statusWarnFill: '#4a3210',
        statusWarnStroke: '#fbbf24',
        statusBadFill: '#4b1d1d',
        statusBadStroke: '#f87171',
      }
    : {
        canvas: '#ffffff',
        panel: '#f9fbfc',
        plot: '#ffffff',
        border: '#cfd8e3',
        grid: '#e9eef3',
        text: '#243341',
        muted: '#607284',
        columnFill: '#d3a36a',
        columnStroke: '#7c4f1d',
        tributaryFill: 'rgba(25, 118, 210, 0.12)',
        tributaryStroke: '#1976d2',
        spacing: '#0f766e',
        diameter: '#b45309',
        center: '#c02659',
        lattice: '#b8c4ce',
        statusGoodFill: '#e7f7ec',
        statusGoodStroke: '#2e7d32',
        statusWarnFill: '#fff4db',
        statusWarnStroke: '#b7791f',
        statusBadFill: '#fde8e8',
        statusBadStroke: '#c53030',
      };

const buildSquareGeometry = (cx, cy, spacingPx) => {
  const circles = [];
  const latticeLines = [];

  [-1, 0, 1].forEach((row) => {
    latticeLines.push({
      x1: cx - spacingPx,
      y1: cy + row * spacingPx,
      x2: cx + spacingPx,
      y2: cy + row * spacingPx,
    });
  });

  [-1, 0, 1].forEach((col) => {
    latticeLines.push({
      x1: cx + col * spacingPx,
      y1: cy - spacingPx,
      x2: cx + col * spacingPx,
      y2: cy + spacingPx,
    });
  });

  [-1, 0, 1].forEach((row) => {
    [-1, 0, 1].forEach((col) => {
      circles.push({
        cx: cx + col * spacingPx,
        cy: cy + row * spacingPx,
        isCenter: row === 0 && col === 0,
      });
    });
  });

  return {
    circles,
    latticeLines,
    tributaryPoints: [
      [cx - spacingPx / 2, cy - spacingPx / 2],
      [cx + spacingPx / 2, cy - spacingPx / 2],
      [cx + spacingPx / 2, cy + spacingPx / 2],
      [cx - spacingPx / 2, cy + spacingPx / 2],
    ],
    spacingStart: { x: cx, y: cy },
    spacingEnd: { x: cx + spacingPx, y: cy },
    spacingArrowY: cy - spacingPx * 0.82,
    gridLabel: 'Square grid',
    formula: 'A = s^2',
  };
};

const buildTriangularGeometry = (cx, cy, spacingPx) => {
  const root3 = Math.sqrt(3);
  const circles = [
    { cx, cy, isCenter: true },
    { cx: cx + spacingPx, cy, isCenter: false },
    { cx: cx - spacingPx, cy, isCenter: false },
    { cx: cx + spacingPx / 2, cy: cy - (root3 * spacingPx) / 2, isCenter: false },
    { cx: cx - spacingPx / 2, cy: cy - (root3 * spacingPx) / 2, isCenter: false },
    { cx: cx + spacingPx / 2, cy: cy + (root3 * spacingPx) / 2, isCenter: false },
    { cx: cx - spacingPx / 2, cy: cy + (root3 * spacingPx) / 2, isCenter: false },
  ];

  const latticeLines = [
    { x1: cx - spacingPx, y1: cy, x2: cx + spacingPx, y2: cy },
    { x1: cx - spacingPx / 2, y1: cy - (root3 * spacingPx) / 2, x2: cx + spacingPx / 2, y2: cy - (root3 * spacingPx) / 2 },
    { x1: cx - spacingPx / 2, y1: cy + (root3 * spacingPx) / 2, x2: cx + spacingPx / 2, y2: cy + (root3 * spacingPx) / 2 },
    { x1: cx, y1: cy, x2: cx - spacingPx / 2, y2: cy - (root3 * spacingPx) / 2 },
    { x1: cx - spacingPx, y1: cy, x2: cx - spacingPx / 2, y2: cy - (root3 * spacingPx) / 2 },
    { x1: cx, y1: cy, x2: cx + spacingPx / 2, y2: cy - (root3 * spacingPx) / 2 },
    { x1: cx, y1: cy, x2: cx + spacingPx / 2, y2: cy + (root3 * spacingPx) / 2 },
    { x1: cx, y1: cy, x2: cx - spacingPx / 2, y2: cy + (root3 * spacingPx) / 2 },
    { x1: cx - spacingPx, y1: cy, x2: cx - spacingPx / 2, y2: cy + (root3 * spacingPx) / 2 },
    { x1: cx + spacingPx, y1: cy, x2: cx + spacingPx / 2, y2: cy - (root3 * spacingPx) / 2 },
    { x1: cx + spacingPx, y1: cy, x2: cx + spacingPx / 2, y2: cy + (root3 * spacingPx) / 2 },
  ];

  return {
    circles,
    latticeLines,
    tributaryPoints: [
      [cx, cy - spacingPx / root3],
      [cx + spacingPx / 2, cy - spacingPx / (2 * root3)],
      [cx + spacingPx / 2, cy + spacingPx / (2 * root3)],
      [cx, cy + spacingPx / root3],
      [cx - spacingPx / 2, cy + spacingPx / (2 * root3)],
      [cx - spacingPx / 2, cy - spacingPx / (2 * root3)],
    ],
    spacingStart: { x: cx, y: cy },
    spacingEnd: { x: cx + spacingPx, y: cy },
    spacingArrowY: cy - (root3 * spacingPx) / 2 - 28,
    gridLabel: 'Triangular grid',
    formula: 'A = sqrt(3) / 2 * s^2',
  };
};

const PriebeAreaRatioSketch = ({
  gridType,
  columnSpacing,
  columnDiameter,
  areaRatioHelper,
  effectiveAreaRatio,
  showSummaryPanel = true,
  showTitle = true,
  showHelperText = true,
}) => {
  const { theme: appTheme } = useContext(Context);
  const colors = useMemo(() => buildColors(appTheme), [appTheme]);
  const [hoveredPart, setHoveredPart] = useState(null);
  const [pinnedPart, setPinnedPart] = useState(null);

  const spacingValue = Number(columnSpacing);
  const diameterValue = Number(columnDiameter);
  const isSquareGrid = gridType === 'square';
  const hasValidSpacing = Number.isFinite(spacingValue) && spacingValue > 0;
  const hasValidDiameter = Number.isFinite(diameterValue) && diameterValue > 0;
  const diameterRatio = hasValidSpacing && hasValidDiameter ? clamp(diameterValue / spacingValue, 0.14, 1) : 0.28;
  const sketchCenterX = 245;
  const sketchCenterY = 220;
  const spacingPx = isSquareGrid ? 124 : 116;
  const diameterPx = spacingPx * diameterRatio;
  const columnRadiusPx = diameterPx / 2;
  const geometry = isSquareGrid ? buildSquareGeometry(sketchCenterX, sketchCenterY, spacingPx) : buildTriangularGeometry(sketchCenterX, sketchCenterY, spacingPx);
  const lowestCircleY = geometry.circles.reduce((maxY, circle) => Math.max(maxY, circle.cy), Number.NEGATIVE_INFINITY);
  const bottomRowCircles = geometry.circles.filter((circle) => Math.abs(circle.cy - lowestCircleY) < 0.001);
  const rightmostCircleX = geometry.circles.reduce((maxX, circle) => Math.max(maxX, circle.cx), Number.NEGATIVE_INFINITY);
  const rightColumnCircles = geometry.circles.filter((circle) => Math.abs(circle.cx - rightmostCircleX) < 0.001);
  const sortedRightColumnCircles = [...rightColumnCircles].sort((a, b) => a.cy - b.cy);
  const topRightCircle = geometry.circles
    .filter((circle) => circle.cx > sketchCenterX && circle.cy < sketchCenterY)
    .sort((a, b) => b.cx - a.cx || a.cy - b.cy)[0];
  const middleRightCircle = geometry.circles
    .filter((circle) => circle.cx > sketchCenterX && Math.abs(circle.cy - sketchCenterY) < 0.001)
    .sort((a, b) => b.cx - a.cx)[0];
  const verticalSpacingStartCircle = sortedRightColumnCircles[0] || { cx: sketchCenterX, cy: sketchCenterY - spacingPx / 2 };
  const verticalSpacingEndCircle = sortedRightColumnCircles[1] || { cx: sketchCenterX, cy: sketchCenterY + spacingPx / 2 };
  const diameterReferenceCircle =
    bottomRowCircles.length > 0
      ? [...bottomRowCircles].sort((a, b) => a.cx - b.cx)[bottomRowCircles.length - 1]
      : { cx: sketchCenterX, cy: sketchCenterY };
  const columnRightX = diameterReferenceCircle.cx + columnRadiusPx;
  const verticalDiameterOffset = isSquareGrid ? 28 : 76;
  const verticalDiameterDimensionX = Math.min(columnRightX + verticalDiameterOffset, 500);
  const verticalDiameterDimensionTopY = diameterReferenceCircle.cy - columnRadiusPx;
  const verticalDiameterDimensionBottomY = diameterReferenceCircle.cy + columnRadiusPx;
  const verticalDiameterLeaderStartX = columnRightX + 2;
  const verticalDiameterLeaderEndX = verticalDiameterDimensionX - 6;
  const verticalDiameterTextX = Math.min(verticalDiameterDimensionX + 14, 510);
  const verticalDiameterTextY = diameterReferenceCircle.cy + 4;
  const verticalSpacingDimensionX = verticalDiameterDimensionX;
  const verticalSpacingDimensionTopY = verticalSpacingStartCircle.cy;
  const verticalSpacingDimensionBottomY = verticalSpacingEndCircle.cy;
  const verticalSpacingLeaderStartX = verticalSpacingStartCircle.cx + columnRadiusPx + 2;
  const verticalSpacingLeaderEndX = verticalSpacingDimensionX - 6;
  const verticalSpacingTextX = verticalDiameterTextX;
  const verticalSpacingTextY = (verticalSpacingDimensionTopY + verticalSpacingDimensionBottomY) / 2 + 4;
  const obliqueSpacingStartCircle = topRightCircle || { cx: sketchCenterX + spacingPx / 2, cy: sketchCenterY - (Math.sqrt(3) * spacingPx) / 2 };
  const obliqueSpacingEndCircle = middleRightCircle || { cx: sketchCenterX + spacingPx, cy: sketchCenterY };
  const obliqueSpacingMidX = (obliqueSpacingStartCircle.cx + obliqueSpacingEndCircle.cx) / 2;
  const obliqueSpacingMidY = (obliqueSpacingStartCircle.cy + obliqueSpacingEndCircle.cy) / 2;
  const obliqueSpacingDx = obliqueSpacingEndCircle.cx - obliqueSpacingStartCircle.cx;
  const obliqueSpacingDy = obliqueSpacingEndCircle.cy - obliqueSpacingStartCircle.cy;
  const obliqueSpacingLength = Math.hypot(obliqueSpacingDx, obliqueSpacingDy) || 1;
  const obliqueSpacingNormalX = obliqueSpacingDy / obliqueSpacingLength;
  const obliqueSpacingNormalY = -obliqueSpacingDx / obliqueSpacingLength;
  const obliqueSpacingAngleDeg = (Math.atan2(obliqueSpacingDy, obliqueSpacingDx) * 180) / Math.PI;
  const obliqueSpacingOffset = 68;
  const obliqueSpacingDimensionStartX = obliqueSpacingStartCircle.cx + obliqueSpacingNormalX * obliqueSpacingOffset;
  const obliqueSpacingDimensionStartY = obliqueSpacingStartCircle.cy + obliqueSpacingNormalY * obliqueSpacingOffset;
  const obliqueSpacingDimensionEndX = obliqueSpacingEndCircle.cx + obliqueSpacingNormalX * obliqueSpacingOffset;
  const obliqueSpacingDimensionEndY = obliqueSpacingEndCircle.cy + obliqueSpacingNormalY * obliqueSpacingOffset;
  const obliqueSpacingTextX = obliqueSpacingMidX + obliqueSpacingNormalX * (obliqueSpacingOffset + 12);
  const obliqueSpacingTextY = obliqueSpacingMidY + obliqueSpacingNormalY * (obliqueSpacingOffset + 12) + 4;

  const viewBoxWidth = showSummaryPanel ? VIEWBOX_WIDTH : PLOT_ONLY_VIEWBOX_WIDTH;
  const activePart = pinnedPart || hoveredPart || 'tributary';

  const status = !hasValidDiameter || !hasValidSpacing
    ? {
        label: 'Enter D and s',
        fill: colors.statusWarnFill,
        stroke: colors.statusWarnStroke,
      }
    : !areaRatioHelper?.spacingGreaterThanDiameter
      ? {
          label: 'Spacing must exceed D',
          fill: colors.statusBadFill,
          stroke: colors.statusBadStroke,
        }
      : !areaRatioHelper?.isWithinChartRange
        ? {
            label: 'Outside chart range',
            fill: colors.statusWarnFill,
            stroke: colors.statusWarnStroke,
          }
        : {
            label: 'Ready for chart lookup',
            fill: colors.statusGoodFill,
            stroke: colors.statusGoodStroke,
          };

  const partDetails = {
    tributary: {
      title: 'Tributary Cell Area',
      color: colors.tributaryStroke,
    },
    column: {
      title: 'Stone Column Area',
      color: colors.center,
    },
  };

  const activeDetail = partDetails[activePart] || partDetails.tributary;

  const bindInteractivePart = (partKey) => ({
    onMouseEnter: () => setHoveredPart(partKey),
    onMouseLeave: () => setHoveredPart(null),
    onFocus: () => setHoveredPart(partKey),
    onBlur: () => setHoveredPart(null),
    onClick: () => setPinnedPart((current) => (current === partKey ? null : partKey)),
    tabIndex: 0,
    role: 'button',
    style: { cursor: 'pointer', outline: 'none' },
  });

  const tributaryPath = geometry.tributaryPoints
    .map(([x, y], index) => `${index === 0 ? 'M' : 'L'} ${x} ${y}`)
    .join(' ')
    .concat(' Z');

  const metricCards = [
    { key: 'grid', label: 'Grid', value: geometry.gridLabel, valueType: 'text' },
    { key: 'spacing', label: 's', value: spacingValue, digits: 3, valueType: 'length' },
    { key: 'diameter', label: 'D', value: diameterValue, digits: 3, valueType: 'length' },
    { key: 'columnArea', label: 'Ac', value: areaRatioHelper?.columnArea, digits: 4, valueType: 'area' },
    { key: 'tributaryArea', label: 'A', value: areaRatioHelper?.tributaryArea, digits: 4, valueType: 'area' },
    { key: 'areaRatio', label: 'A/Ac', value: effectiveAreaRatio, digits: 4, valueType: 'number' },
    { key: 'replacementRatio', label: 'Ac/A', value: areaRatioHelper?.replacementRatio, digits: 4, valueType: 'number' },
  ];

  const renderMetricLabel = (card, x, y) => {
    if (card.label === 'Ac') {
      return (
        <text x={x} y={y} fontSize="11" fill={colors.muted}>
          <tspan>A</tspan>
          <tspan baselineShift="sub" fontSize="9">
            c
          </tspan>
        </text>
      );
    }

    if (card.label === 'A/Ac') {
      return (
        <text x={x} y={y} fontSize="11" fill={colors.muted}>
          <tspan>A/A</tspan>
          <tspan baselineShift="sub" fontSize="9">
            c
          </tspan>
        </text>
      );
    }

    if (card.label === 'Ac/A') {
      return (
        <text x={x} y={y} fontSize="11" fill={colors.muted}>
          <tspan>A</tspan>
          <tspan baselineShift="sub" fontSize="9">
            c
          </tspan>
          <tspan>/A</tspan>
        </text>
      );
    }

    return (
      <text x={x} y={y} fontSize="11" fill={colors.muted}>
        {card.label}
      </text>
    );
  };

  const renderMetricValue = (card, x, y) => {
    if (card.valueType === 'text') {
      return (
        <text x={x} y={y} fontSize="13" fontWeight="700" fill={colors.text}>
          {card.value}
        </text>
      );
    }

    if (card.valueType === 'length') {
      return (
        <text x={x} y={y} fontSize="13" fontWeight="700" fill={colors.text}>
          <tspan>{formatValue(card.value, card.digits)}</tspan>
          <tspan> m</tspan>
        </text>
      );
    }

    if (card.valueType === 'area') {
      return (
        <text x={x} y={y} fontSize="13" fontWeight="700" fill={colors.text}>
          <tspan>{formatValue(card.value, card.digits)}</tspan>
          <tspan> m</tspan>
          <tspan baselineShift="super" fontSize="10">
            2
          </tspan>
        </text>
      );
    }

    return (
      <text x={x} y={y} fontSize="13" fontWeight="700" fill={colors.text}>
        {formatValue(card.value, card.digits)}
      </text>
    );
  };

  const drawMetricCard = (card, index) => {
    const col = index % 2;
    const row = Math.floor(index / 2);
    const x = SUMMARY_X + col * (SUMMARY_CARD_WIDTH + SUMMARY_CARD_COL_GAP);
    const y = SUMMARY_METRICS_Y + row * (SUMMARY_CARD_HEIGHT + SUMMARY_CARD_ROW_GAP);
    return (
      <g key={card.key}>
        <rect x={x} y={y} width={SUMMARY_CARD_WIDTH} height={SUMMARY_CARD_HEIGHT} rx="8" fill={colors.canvas} stroke={colors.border} />
        {renderMetricLabel(card, x + 10, y + 14)}
        {renderMetricValue(card, x + 10, y + 33)}
      </g>
    );
  };

  const renderFocusBody = () => {
    if (activePart === 'column') {
      return (
        <foreignObject x={SUMMARY_X + 12} y={SUMMARY_FOCUS_Y + 34} width={SUMMARY_WIDTH - 24} height={SUMMARY_FOCUS_HEIGHT - 44}>
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            style={{
              fontFamily: 'Lato, sans-serif',
              color: colors.text,
              fontSize: '12px',
              lineHeight: 1.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <math display="block" style={{ margin: 0 }}>
              <mrow>
                <msub>
                  <mi>A</mi>
                  <mi>c</mi>
                </msub>
                <mo>=</mo>
                <mfrac>
                  <mi>&pi;</mi>
                  <mn>4</mn>
                </mfrac>
                <msup>
                  <mi>D</mi>
                  <mn>2</mn>
                </msup>
                <mo>=</mo>
                <mn>{formatValue(areaRatioHelper?.columnArea, 4)}</mn>
                <mspace width="0.2em" />
                <msup>
                  <mi>m</mi>
                  <mn>2</mn>
                </msup>
              </mrow>
            </math>
          </div>
        </foreignObject>
      );
    }

    return (
      <foreignObject x={SUMMARY_X + 12} y={SUMMARY_FOCUS_Y + 34} width={SUMMARY_WIDTH - 24} height={SUMMARY_FOCUS_HEIGHT - 44}>
        <div
          xmlns="http://www.w3.org/1999/xhtml"
          style={{
            fontFamily: 'Lato, sans-serif',
            color: colors.text,
            fontSize: '12px',
            lineHeight: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <math display="block" style={{ margin: 0 }}>
            <mrow>
              <mi>A</mi>
              <mo>=</mo>
              {geometry.gridLabel === 'Square grid' ? (
                <msup>
                  <mi>s</mi>
                  <mn>2</mn>
                </msup>
              ) : (
                <>
                  <mfrac>
                    <msqrt>
                      <mn>3</mn>
                    </msqrt>
                    <mn>2</mn>
                  </mfrac>
                  <msup>
                    <mi>s</mi>
                    <mn>2</mn>
                  </msup>
                </>
              )}
              <mo>=</mo>
              <mn>{formatValue(areaRatioHelper?.tributaryArea, 4)}</mn>
              <mspace width="0.2em" />
              <msup>
                <mi>m</mi>
                <mn>2</mn>
              </msup>
            </mrow>
          </math>
        </div>
      </foreignObject>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <svg
        viewBox={`0 0 ${viewBoxWidth} ${VIEWBOX_HEIGHT}`}
        style={{ display: 'block', width: '100%', height: 'auto', borderRadius: 10, background: colors.canvas }}
        role="img"
        aria-label="Area ratio layout sketch"
      >
        <defs>
          <marker id="priebe-ar-open-r" markerWidth="6" markerHeight="6" refX="4.5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6" fill="none" stroke="currentColor" strokeWidth="1" />
          </marker>
          <marker id="priebe-ar-open-l" markerWidth="6" markerHeight="6" refX="1.5" refY="3" orient="auto">
            <path d="M6,0 L0,3 L6,6" fill="none" stroke="currentColor" strokeWidth="1" />
          </marker>
          <pattern id="priebe-soil-dots" patternUnits="userSpaceOnUse" width="16" height="16">
            <circle cx="3" cy="3" r="1" fill={colors.grid} />
            <circle cx="11" cy="8" r="1" fill={colors.grid} />
            <circle cx="7" cy="13" r="1" fill={colors.grid} />
          </pattern>
        </defs>

        <rect x="0" y="0" width={viewBoxWidth} height={VIEWBOX_HEIGHT} fill={colors.canvas} rx="10" />
        <g transform={`translate(0 ${CONTENT_Y_SHIFT})`}>
          <rect x="20" y="20" width="510" height="390" rx="12" fill={colors.plot} stroke={colors.border} />
          {showSummaryPanel && <rect x="546" y="20" width="294" height="390" rx="12" fill={colors.panel} stroke={colors.border} />}

          {[...Array(10)].map((_, index) => (
            <line
              key={`grid-v-${index}`}
              x1={48 + index * 46}
              y1="38"
              x2={48 + index * 46}
              y2="392"
              stroke={colors.grid}
              strokeWidth="1"
            />
          ))}
          {[...Array(8)].map((_, index) => (
            <line
              key={`grid-h-${index}`}
              x1="38"
              y1={62 + index * 42}
              x2="512"
              y2={62 + index * 42}
              stroke={colors.grid}
              strokeWidth="1"
            />
          ))}

          <rect x="38" y="38" width="474" height="354" fill="url(#priebe-soil-dots)" opacity="0.45" />

          {geometry.latticeLines.map((line, index) => (
            <line
              key={`lattice-${index}`}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke={colors.lattice}
              strokeWidth="1.1"
              strokeDasharray="4 4"
            />
          ))}

          <g {...bindInteractivePart('tributary')}>
            <path
              d={tributaryPath}
              fill={colors.tributaryFill}
              stroke={colors.tributaryStroke}
              strokeWidth={activePart === 'tributary' ? 2.6 : 1.8}
              strokeDasharray={geometry.gridLabel === 'Square grid' ? 'none' : '6 4'}
            />
          </g>

          {geometry.circles.map((circle, index) => (
            <g key={`column-${index}`} {...(circle.isCenter ? bindInteractivePart('column') : {})}>
              <circle
                cx={circle.cx}
                cy={circle.cy}
                r={columnRadiusPx}
                fill={circle.isCenter ? colors.center : colors.columnFill}
                fillOpacity={circle.isCenter ? 0.2 : 0.9}
                stroke={circle.isCenter ? colors.center : colors.columnStroke}
                strokeWidth={circle.isCenter && activePart === 'column' ? 2.8 : 1.8}
              />
              <circle
                cx={circle.cx}
                cy={circle.cy}
                r="2.6"
                fill={circle.isCenter ? colors.center : colors.columnStroke}
              />
            </g>
          ))}

          {isSquareGrid ? (
            <g color="#111111">
              <line
                x1={verticalSpacingDimensionX}
                y1={verticalSpacingDimensionTopY}
                x2={verticalSpacingDimensionX}
                y2={verticalSpacingDimensionBottomY}
                stroke="currentColor"
                strokeWidth="1.2"
                markerStart="url(#priebe-ar-open-l)"
                markerEnd="url(#priebe-ar-open-r)"
              />
              <line
                x1={verticalSpacingLeaderStartX}
                y1={verticalSpacingDimensionTopY}
                x2={verticalSpacingLeaderEndX}
                y2={verticalSpacingDimensionTopY}
                stroke="currentColor"
                strokeWidth="1"
              />
              <line
                x1={verticalSpacingLeaderStartX}
                y1={verticalSpacingDimensionBottomY}
                x2={verticalSpacingLeaderEndX}
                y2={verticalSpacingDimensionBottomY}
                stroke="currentColor"
                strokeWidth="1"
              />
              <text x={verticalSpacingTextX} y={verticalSpacingTextY} textAnchor="start" fontSize="12" fontWeight="700" fill="currentColor">
                s = {formatValue(spacingValue, 2, 'm')}
              </text>
            </g>
          ) : (
            <g color="#111111">
              <line
                x1={obliqueSpacingDimensionStartX}
                y1={obliqueSpacingDimensionStartY}
                x2={obliqueSpacingDimensionEndX}
                y2={obliqueSpacingDimensionEndY}
                stroke="currentColor"
                strokeWidth="1.2"
                markerStart="url(#priebe-ar-open-l)"
                markerEnd="url(#priebe-ar-open-r)"
              />
              <line
                x1={obliqueSpacingStartCircle.cx}
                y1={obliqueSpacingStartCircle.cy}
                x2={obliqueSpacingDimensionStartX}
                y2={obliqueSpacingDimensionStartY}
                stroke="currentColor"
                strokeWidth="1"
              />
              <line
                x1={obliqueSpacingEndCircle.cx}
                y1={obliqueSpacingEndCircle.cy}
                x2={obliqueSpacingDimensionEndX}
                y2={obliqueSpacingDimensionEndY}
                stroke="currentColor"
                strokeWidth="1"
              />
              <text
                x={obliqueSpacingTextX}
                y={obliqueSpacingTextY}
                textAnchor="middle"
                fontSize="12"
                fontWeight="700"
                fill="currentColor"
                transform={`rotate(${obliqueSpacingAngleDeg} ${obliqueSpacingTextX} ${obliqueSpacingTextY})`}
              >
                s = {formatValue(spacingValue, 2, 'm')}
              </text>
            </g>
          )}

          <g color="#111111">
            <line
              x1={verticalDiameterDimensionX}
              y1={verticalDiameterDimensionTopY}
              x2={verticalDiameterDimensionX}
              y2={verticalDiameterDimensionBottomY}
              stroke="currentColor"
              strokeWidth="1.2"
              markerStart="url(#priebe-ar-open-l)"
              markerEnd="url(#priebe-ar-open-r)"
            />
            <line
              x1={verticalDiameterLeaderStartX}
              y1={verticalDiameterDimensionTopY}
              x2={verticalDiameterLeaderEndX}
              y2={verticalDiameterDimensionTopY}
              stroke="currentColor"
              strokeWidth="1"
            />
            <line
              x1={verticalDiameterLeaderStartX}
              y1={verticalDiameterDimensionBottomY}
              x2={verticalDiameterLeaderEndX}
              y2={verticalDiameterDimensionBottomY}
              stroke="currentColor"
              strokeWidth="1"
            />
            <text x={verticalDiameterTextX} y={verticalDiameterTextY} textAnchor="start" fontSize="12" fontWeight="700" fill="currentColor">
              D = {formatValue(diameterValue, 2, 'm')}
            </text>
          </g>

          {showSummaryPanel && (
            <>
              <rect x={SUMMARY_X} y={SUMMARY_BADGE_Y} width={SUMMARY_WIDTH} height="26" rx="13" fill={status.fill} stroke={status.stroke} />
              <text x={SUMMARY_X + SUMMARY_WIDTH / 2} y={SUMMARY_BADGE_Y + 17} textAnchor="middle" fontSize="12" fontWeight="700" fill={status.stroke}>
                {status.label}
              </text>

              {metricCards.map(drawMetricCard)}

              <rect x={SUMMARY_X} y={SUMMARY_FOCUS_Y} width={SUMMARY_WIDTH} height={SUMMARY_FOCUS_HEIGHT} rx="10" fill={colors.canvas} stroke={activeDetail.color} />
              <text x={SUMMARY_X + 14} y={SUMMARY_FOCUS_Y + 22} fontSize="14" fontWeight="700" fill={activeDetail.color}>
                {activeDetail.title}
              </text>
              {renderFocusBody()}
            </>
          )}
        </g>
        {showTitle && (
          <text x="275" y="12" textAnchor="middle" fontSize="14" fontWeight="700" fill={colors.text}>
            Plan layout sketch
          </text>
        )}
        {showSummaryPanel && showTitle && (
          <text x="693" y="12" textAnchor="middle" fontSize="14" fontWeight="700" fill={colors.text}>
            Layout summary
          </text>
        )}
        {showHelperText && (
          <text x="20" y="424" fontSize="10.5" fill={colors.muted}>
            Hover or tap the shaded cell or the center column.
          </text>
        )}
      </svg>
    </Box>
  );
};

export default React.memo(PriebeAreaRatioSketch);
